import { ReactNode, useState, useEffect, useCallback, ChangeEvent } from "react";

import i18n from "translation";

import { $createParagraphNode, $getSelection, $isParagraphNode, $isRangeSelection, $isTextNode, $setSelection, $createTextNode,
          CAN_REDO_COMMAND, CAN_UNDO_COMMAND, CommandListenerPriority, ElementFormatType, LexicalNode,
          FORMAT_ELEMENT_COMMAND, FORMAT_TEXT_COMMAND, INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND,
          REDO_COMMAND, UNDO_COMMAND } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHeadingNode, $isHeadingNode, HeadingNode, HeadingTagType, $createQuoteNode } from "@lexical/rich-text";
import { $setBlocksType, $getSelectionStyleValueForProperty, $patchStyleText } from "@lexical/selection";
import { $isListItemNode, ListType, ListNode, INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_CHECK_LIST_COMMAND } from "@lexical/list";
import { mergeRegister, $getNearestBlockElementAncestorOrThrow, $getNearestNodeOfType } from "@lexical/utils";
import { $createLinkNode } from '@lexical/link';

import { Toolbar, IconButton, Divider, useTheme, TextField, Popover, Box } from "@mui/material";
import { UndoRounded, RedoRounded, LooksOneRounded, LooksTwoRounded, Looks3Rounded, Looks4Rounded,
          Looks5Rounded, Looks6Rounded, LocalParkingRounded, FormatBoldRounded, FormatItalicRounded,
          FormatUnderlinedRounded, StrikethroughS, FormatColorTextRounded, BorderColorRounded,
          FormatColorFillRounded, FormatAlignLeftRounded, FormatAlignCenterRounded, FormatAlignRightRounded,
          FormatAlignJustifyRounded, FormatIndentIncreaseRounded, FormatIndentDecreaseRounded,
          FormatListBulletedRounded, FormatListNumberedRounded, InsertLinkRounded, FormatQuoteRounded,
          FormatClearRounded, Close, CheckCircleRounded, CheckBoxOutlineBlankRounded } from "@mui/icons-material";

import PopoverColorPicker, { AnyColor } from "components/uiElements/inputs/colorSelector/parts/PopoverColorPicker";

import FontSizeSelect from "components/uiElements/inputs/FontSizeSelect";
import MenuDropdown from "./ToolbarMenu";

interface MenuToolbarItem {
  label?: string;
  identifier: string | HeadingTagType | ElementFormatType | ListType;
  domElement: JSX.Element | string | null;
}

const LowPriority: CommandListenerPriority = 1;
const textElementOptions: Array<MenuToolbarItem> = [
  { label: i18n.t("widget-types.rich-text-editor.settings.text-type.paragraph"), identifier: "p", domElement: <LocalParkingRounded /> },
  { label: i18n.t("widget-types.rich-text-editor.settings.text-type.heading-1"), identifier: "h1", domElement: <LooksOneRounded /> },
  { label: i18n.t("widget-types.rich-text-editor.settings.text-type.heading-2"), identifier: "h2", domElement: <LooksTwoRounded /> },
  { label: i18n.t("widget-types.rich-text-editor.settings.text-type.heading-3"), identifier: "h3", domElement: <Looks3Rounded /> },
  { label: i18n.t("widget-types.rich-text-editor.settings.text-type.heading-4"), identifier: "h4", domElement: <Looks4Rounded /> },
  { label: i18n.t("widget-types.rich-text-editor.settings.text-type.heading-5"), identifier: "h5", domElement: <Looks5Rounded /> },
  { label: i18n.t("widget-types.rich-text-editor.settings.text-type.heading-6"), identifier: "h6", domElement: <Looks6Rounded /> },
];
const textAlignOptions: Array<MenuToolbarItem> = [
  { identifier: "left", domElement: <FormatAlignLeftRounded /> },
  { identifier: "center", domElement: <FormatAlignCenterRounded /> },
  { identifier: "right", domElement: <FormatAlignRightRounded /> },
  { identifier: "justify", domElement: <FormatAlignJustifyRounded /> },
];
const listTypeOptions: Array<MenuToolbarItem> = [
  { identifier: "bullet", domElement: <FormatListBulletedRounded /> },
  { identifier: "number", domElement: <FormatListNumberedRounded /> },
  { identifier: "check", domElement: <CheckBoxOutlineBlankRounded /> },
];

const fontSelectStyle = {
  "& .MuiFormControl-root ": {
    ".MuiInputBase-root": {
      paddingTop: 0,
      paddingBottom: 0,
      background: "rgba(255, 255, 255, .5)",
      "input": {
        textAlign: "center",
        paddingTop: "5px",
        paddingBottom: "5px",
      },
      "> .MuiOutlinedInput-notchedOutline": {
        border: "1px solid rgba(0, 0, 0, 0.23)"
      }
    }
  }
};

export default function ToolbarPlugin(props: { setBackgroundColor: (color: string) => void, backgroundColor: string }): ReactNode {
  const [editor] = useLexicalComposerContext();
  const theme = useTheme();
  const activeButtonBgColor = theme.palette.action.selected;
  
  // Update toolbar selected button state
  const [isBoldActive, setIsBoldActive] = useState<boolean>(false);
  const [isItalicActive, setIsItalicActive] = useState<boolean>(false);
  const [isUnderlineActive, setIsUnderlineActive] = useState<boolean>(false);
  const [isStrikethroughActive, setIsStrikethroughActive] = useState<boolean>(false);
  const [textType, setTextType] = useState<string | undefined>("p");
  const [fontSize, setFontSize] = useState<string | null>(`${theme.typography.body1.fontSize}`);
  
  const [textAlign, setTextAlign] = useState<string>("left");
  const [listType, setListType] = useState<string>("bullet");

  const [textColor, setTextColor] = useState<string>("");
  const [textColorAnchor, setTextColorAnchor] = useState<HTMLElement | null>(null);
  const [isTextColorOpen, setIsTextColorOpen] = useState(false);

  const [textHighlightColor, setTextHighlightColor] = useState<string>("");
  const [textHighlightColorAnchor, setTextHighlightColorAnchor] = useState<HTMLElement | null>(null);
  const [isTextHighlightColorOpen, setIsTextHighlightColorOpen] = useState(false);

  const [backgroundColor, setBackgroundColor] = useState<string>(props.backgroundColor ?? "");
  const [backgroundColorAnchor, setBackgroundColorAnchor] = useState<HTMLElement | null>(null);
  const [isBackgroundColorOpen, setIsBackgroundColorOpen] = useState(false);

  const [insertLinkAnchor, setInsertLinkAnchor] = useState<HTMLElement | null>(null);
  const [link, setLink] = useState("");
  const insertLinkOpen = Boolean(insertLinkAnchor);

  const handleFormatFontSize = (_fontSize: string, hasCustomValue?: boolean) => {
    setFontSize(_fontSize);

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) && _fontSize.length > 0) {
        if (hasCustomValue) {
          $patchStyleText(selection, {"font-size": `${_fontSize}px`});
          return;
        }
        $patchStyleText(selection, {"font-size": `${fontSize}px`});
      }
    });
  };

  const handleCloseFontSelector = (reason: string) => {
    if (reason === "escape") {
      editor.update(() => {
        const selection = $getSelection();
        $setSelection(selection);
      });
    }
  };

  const formatTextElementType = (identifier: string | HeadingTagType) => {
    switch(identifier) {
      case "p":
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) $setBlocksType(selection, () => $createParagraphNode());
          editor.getRootElement()?.focus();
        });
        return;
      default:
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createHeadingNode(identifier as HeadingTagType));
          }
          editor.getRootElement()?.focus();
        });
        return;
    }
  };

  const handleFormatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
  }

  const handleFormatItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
  }

  const handleFormatUnderline = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
  }

  const handleFormatStrikethrough = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
  }

  const formatTextAlign = (identifier: string | ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, identifier as ElementFormatType);
  };

  const handleFormatIndent = (identifier: string) => {
    if (identifier === "indent") {
      editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
      return;
    }
    if (identifier === "dedent") {
      editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
      return;
    }
  }

  const formatListType = (identifier: string | ListType) => {
    switch(identifier) {
      case "bullet":
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        return;
      case "number":
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        return;
      case "check":
        editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
        return;
      default:
        return;
    }
  };

  const handleOpenInsertLink = (event: React.MouseEvent<HTMLButtonElement>) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const selectionAnchorKey = selection.anchor.getNode().__key;
        const selectionDOMElement = editor.getElementByKey(selectionAnchorKey);
        // selection is always a span, so we check to see if the parent is an anchor element
        const currentLink = selectionDOMElement?.parentElement?.getAttribute("href")?.split("https://")[1] ?? "";
        
        setLink(currentLink);
        setInsertLinkAnchor(selectionDOMElement);
      }
    });
  };

  const handleCancelInsertLink = () => setInsertLinkAnchor(null);

  const handleSaveInsertLink = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection) && link.length > 0) {          
        const linkText = selection?.getTextContent().length > 0 ?
          selection?.getTextContent() : `https://${link}`;

        const linkNode = $createLinkNode(`https://${link}`,
          {rel: "noopener noreferrer", target: "_blank", title: ""}
        ).append($createTextNode(linkText));

        selection.insertNodes([linkNode]);
      }
    });
    setInsertLinkAnchor(null);
  };

  const handleFormatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) $setBlocksType(selection, () => $createQuoteNode());
    });
  };

  const handleClearFormat = () => {
    editor.update(() => {
      const selection = $getSelection();
      
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();

        // if nothing is selected/highlighted do nothing
        if (selection.isCollapsed()) {
          return;
        }

        nodes.forEach(node => {
          if ($isTextNode(node)) {
            node.setFormat(0);
            node.setStyle("");
            if (!$isHeadingNode(node.getParent())) {
              node.getParent()?.replace($createParagraphNode(), true);
            }
          }
        });
      }
    });
  };

  const handleOpenTextColor = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isTextColorOpen) {
      setTextColorAnchor(event.currentTarget as HTMLElement);
      setIsTextColorOpen(true);
    }
  };

  const handleTextColorChange = (color: AnyColor) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) $patchStyleText(selection, {"color": color as string});
    });
    setTextColor(color as string);
  };

  const handlePickerClose = () => {
    setTextColorAnchor(null);
    setIsTextColorOpen(false);
    editor.update(() => {
      // Gets the latest cursor location from the editor state
      const prevSelection = editor.getEditorState()._selection?.clone();
      // After closing color picker, sets the cursor location from the most recent editor state
      if ($isRangeSelection(prevSelection)) $setSelection(prevSelection);
    });
  };

  const handleOpenTextHighlightColor = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isTextHighlightColorOpen) {
      setTextHighlightColorAnchor(event.currentTarget as HTMLElement);
      setIsTextHighlightColorOpen(true);
    }
  };

  const handleTextHighlightColorChange = (color: AnyColor) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, {"background-color": color as string});
      }
    });
    setTextHighlightColor(color as string);
  };

  const handleHighlightPickerClose = () => {
    setTextHighlightColorAnchor(null);
    setIsTextHighlightColorOpen(false);
    editor.update(() => {
      // Gets the latest cursor location from the editor state
      const prevSelection = editor.getEditorState()._selection?.clone();
      // After closing color picker, sets the cursor location from the most recent editor state
      if ($isRangeSelection(prevSelection)) $setSelection(prevSelection);
    });
  };

  const handleOpenBackgroundColor = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isBackgroundColorOpen) {
      setBackgroundColorAnchor(event.currentTarget as HTMLElement);
      setIsBackgroundColorOpen(true);
    }
  };

  const handleBackgroundColorChange = (color: AnyColor) => {
    props.setBackgroundColor(color as string);
    setBackgroundColor(color as string);
  };

  const handleBackgroundColorPickerClose = () => {
    setBackgroundColorAnchor(null);
    setIsBackgroundColorOpen(false);
  };

  // Update the toolbar button states
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if (selection && $isRangeSelection(selection)) {
      // The selection will always be of type "element" if the line it's on has no text.
      // The selection will always be of type "text" if the line it's on DOES have text.
      // For that reason, we need to get the parent of the selection.
      // Get the node of the selection, & then get its parent
      const parent = selection.anchor.getNode().getParent();
      // This series of state setters are relevant to inline elements, i.e. <strong>, <i>, <u>, etc
      setFontSize($getSelectionStyleValueForProperty(selection, "font-size", `${theme.typography.body1.fontSize}`).replace("px", ""));
      setTextColor($getSelectionStyleValueForProperty(selection, "color", textColor));
      setTextHighlightColor($getSelectionStyleValueForProperty(selection, "background-color", textHighlightColor));
      setIsBoldActive(selection.hasFormat("bold"));
      setIsItalicActive(selection.hasFormat("italic"));
      setIsUnderlineActive(selection.hasFormat("underline"));
      setIsStrikethroughActive(selection.hasFormat("strikethrough"));

      // Checking if parent of selected node exists to handle update button icon displayed
      // This is relevant to block level elements & their display/styling
      if (parent) {
        setTextAlign(parent.getFormatType());

        // If selection is a list item (selection can never be just a list (<ul>/<ol>), rather a list item (<li>))
        // If selection is NOT a list item, show bullet list (ul) as default display option
        if ($isListItemNode(parent)) {
          const ancestorListNode = $getNearestNodeOfType(parent, ListNode);
          if (ancestorListNode) {
            setListType(ancestorListNode.getListType());
          }
        } else {
          setListType("bullet");
        }

        const allNodes = selection.getNodes();
        let firstBlockNode:LexicalNode = $getNearestBlockElementAncestorOrThrow(allNodes[0]);
        /* 
        This algorithm checks if the first node element type in selection list matches the
        other node element types in the selection list. If it does, the text menu item will show
        the single matching element. If not, the text menu will show nothing.
        */
        for (let i = 1; i < allNodes.length; i++) {
          const currentNode = allNodes[i];
          if (currentNode.getType() !== "text") {
            if (currentNode.getType() !== firstBlockNode.getType() ||
            // The other side of the || operator compares heading tag types.
            // In Lexical, only heading nodes have a "__tag" property on them, which is probably an oversight
            (currentNode as HeadingNode).__tag !== (currentNode as HeadingNode).__tag) {
              setTextType(undefined);
              return;
            }
          }
        }
        if ($isHeadingNode(firstBlockNode)) {
          setTextType(firstBlockNode.__tag);
          return;
        }
        if ($isParagraphNode(firstBlockNode)) {
          setTextType("p");
          return;
        }
        setTextType(firstBlockNode.getType());
      }

    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        editorState.read(() => {
          updateToolbar();
        })
      }),
      editor.registerCommand(CAN_UNDO_COMMAND, payload => false, LowPriority),
      editor.registerCommand(CAN_REDO_COMMAND, payload => false, LowPriority),
    );
  }, [editor, updateToolbar])

  return (
    <Toolbar sx={{ backgroundColor: theme.colors.primary.lighter, flexWrap: "wrap", width: "100%" }}>
      <IconButton onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}><UndoRounded /></IconButton>
      <IconButton onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}><RedoRounded /></IconButton>
      <Divider flexItem orientation="vertical" variant="middle" />
      <MenuDropdown menuOptions={textElementOptions} onClick={formatTextElementType} showItemLabel={true} selectionValue={textType} />
      <FontSizeSelect
        style={fontSelectStyle}
        fontSize={fontSize}
        onInputChange={handleFormatFontSize}
        onClose={handleCloseFontSelector}
      />
      <IconButton sx={{ backgroundColor: isBoldActive ? activeButtonBgColor : "unset" }} onClick={handleFormatBold}><FormatBoldRounded /></IconButton>
      <IconButton sx={{ backgroundColor: isItalicActive ? activeButtonBgColor : "unset" }} onClick={handleFormatItalic}><FormatItalicRounded /></IconButton>
      <IconButton sx={{ backgroundColor: isUnderlineActive ? activeButtonBgColor : "unset" }} onClick={handleFormatUnderline}><FormatUnderlinedRounded /></IconButton>
      <IconButton sx={{ backgroundColor: isStrikethroughActive ? activeButtonBgColor : "unset" }} onClick={handleFormatStrikethrough}><StrikethroughS /></IconButton>
      <IconButton
        sx={{
          "&.MuiIconButton-root::after": {
            content: `""`,
            width: "53%",
            height: "5px",
            position: "absolute",
            borderRadius: "10px",
            bottom: "7px",
            backgroundColor: textColor,
          }
        }}
        onClick={handleOpenTextColor}>
        <FormatColorTextRounded />
      </IconButton>
      <PopoverColorPicker
        anchor={textColorAnchor as HTMLElement}
        color={textColor}
        defaultColor={theme.palette.text.primary}
        isOpen={isTextColorOpen}
        showInput={true}
        onChange={handleTextColorChange}
        onClose={handlePickerClose} />
      <IconButton
        sx={{
          "&.MuiIconButton-root::after": {
            content: `""`,
            width: "53%",
            height: "5px",
            position: "absolute",
            borderRadius: "10px",
            bottom: "7px",
            backgroundColor: textHighlightColor,
          }
        }}
        onClick={handleOpenTextHighlightColor}>
          <BorderColorRounded />
      </IconButton>
      <PopoverColorPicker
        anchor={textHighlightColorAnchor as HTMLElement}
        color={textHighlightColor}
        defaultColor={theme.colors.primary.main}
        isOpen={isTextHighlightColorOpen}
        showInput={true}
        onChange={handleTextHighlightColorChange}
        onClose={handleHighlightPickerClose} />
      <IconButton
        sx={{
          "&.MuiIconButton-root::after": {
            content: `""`,
            width: "53%",
            height: "5px",
            position: "absolute",
            borderRadius: "10px",
            bottom: "7px",
            backgroundColor: backgroundColor,
          }
        }}
        onClick={handleOpenBackgroundColor}>
          <FormatColorFillRounded />
      </IconButton>
      <PopoverColorPicker
        anchor={backgroundColorAnchor as HTMLElement}
        color={backgroundColor}
        defaultColor={"#fff"}
        isOpen={isBackgroundColorOpen}
        showInput={true}
        onChange={handleBackgroundColorChange}
        onClose={handleBackgroundColorPickerClose} />
      <Divider flexItem orientation="vertical" variant="middle" />
      <IconButton onClick={handleOpenInsertLink}><InsertLinkRounded /></IconButton>
      <Popover
        open={insertLinkOpen}
        anchorEl={insertLinkAnchor}
        hideBackdrop={true}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <TextField
          autoComplete="off"
          value={link}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            let inputValue = event.target.value;
            const pattern = /^https?:\/\//;
            const matchedPattern = inputValue.match(pattern)?.[0];

            inputValue = matchedPattern === null ? inputValue : inputValue.replace((matchedPattern as string), '');
            
            setLink(inputValue);
          }}
          slotProps={{
            input: {
              startAdornment: "https://",
              endAdornment:
                <Box sx={{ display: "flex" }}>
                  <IconButton onClick={handleCancelInsertLink} sx={{ background: theme.colors.error.lighter, marginRight: "10px" }}>
                    <Close fontSize="small" sx={{ color: "red" }} />
                  </IconButton>
                  <IconButton onClick={handleSaveInsertLink} sx={{ background: theme.colors.success.light }}>
                    <CheckCircleRounded fontSize="small" />
                  </IconButton>
                </Box>
            }
          }}
        />
      </Popover>
      <IconButton sx={{ backgroundColor: textType === "quote" ? activeButtonBgColor : "unset" }} onClick={handleFormatQuote}><FormatQuoteRounded /></IconButton>
      <MenuDropdown menuOptions={textAlignOptions} onClick={formatTextAlign}
        dropdDownDirection="row" selectionValue={textAlign === "" ? "left" : textAlign} />
      <IconButton onClick={() => handleFormatIndent("indent")}><FormatIndentIncreaseRounded /></IconButton>
      <IconButton onClick={() => handleFormatIndent("dedent")}><FormatIndentDecreaseRounded /></IconButton>
      <MenuDropdown menuOptions={listTypeOptions} onClick={formatListType}
        dropdDownDirection="row" selectionValue={listType} />
      <Divider flexItem orientation="vertical" variant="middle" />
      <IconButton onClick={handleClearFormat}><FormatClearRounded /></IconButton>
    </Toolbar>
  );
}